import { useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { esquemaClave } from '@compranorte/contracts'
import { useRestablecer } from '../api/hooks'
import { MarcoAcceso } from '../componentes/MarcoAcceso'
import { IconoCheck } from '../componentes/icons'
import { Aviso } from '../componentes/ui'

const MINIMO_CLAVE = 12

/**
 * Fija la contraseña nueva con el token del correo.
 *
 * El servidor cierra TODAS las sesiones de la cuenta al aplicarla: si alguien
 * tenía acceso robado, este es el momento en que lo pierde. Por eso la pantalla
 * termina en "entre de nuevo" y no en una sesión abierta.
 */
export function Restablecer() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const restablecer = useRestablecer()
  const [clave, setClave] = useState('')
  const [repetir, setRepetir] = useState('')
  const [campos, setCampos] = useState<Record<string, string>>({})

  function enviar(ev: FormEvent) {
    ev.preventDefault()
    if (restablecer.isPending) return

    const errores: Record<string, string> = {}
    const revision = esquemaClave.safeParse(clave)
    if (!revision.success) {
      errores['clave'] = revision.error.issues[0]?.message ?? 'La contraseña no es válida'
    }
    if (clave && clave !== repetir) errores['repetir'] = 'Las contraseñas no coinciden.'

    if (Object.keys(errores).length > 0) {
      setCampos(errores)
      return
    }

    setCampos({})
    restablecer.mutate({ token, clave })
  }

  const errorClaveApi = restablecer.error?.detalleDe('clave')

  if (restablecer.isSuccess) {
    return (
      <MarcoAcceso>
        <h1 className="text-[17px] font-semibold text-acero-900 flex items-center gap-2">
          <IconoCheck size={18} className="text-ok-500" />
          Contraseña actualizada
        </h1>
        <div className="mt-3">
          <Aviso tono="ok">{restablecer.data.mensaje}</Aviso>
        </div>
        <p className="ayuda mt-3">
          Por seguridad se cerraron todas las sesiones abiertas de su cuenta, incluidas las de otros
          dispositivos.
        </p>
        <Link to="/entrar" className="btn btn-accion w-full mt-4">
          Entrar
        </Link>
      </MarcoAcceso>
    )
  }

  // Sin token no hay nada que hacer acá: se manda a pedir uno nuevo.
  if (!token) {
    return (
      <MarcoAcceso>
        <h1 className="text-[17px] font-semibold text-acero-900">Enlace incompleto</h1>
        <div className="mt-3">
          <Aviso tono="error">
            A esta dirección le falta el código del enlace. Cópielo completo desde el correo, o pida
            uno nuevo.
          </Aviso>
        </div>
        <Link to="/recuperar" className="btn btn-accion w-full mt-4">
          Pedir un enlace nuevo
        </Link>
      </MarcoAcceso>
    )
  }

  return (
    <MarcoAcceso
      pie={
        <Link to="/entrar" className="text-[12px] text-acero-500 hover:text-acero-800">
          ← Volver a entrar
        </Link>
      }
    >
      <h1 className="text-[17px] font-semibold text-acero-900">Cree una contraseña nueva</h1>
      <p className="text-[12.5px] text-acero-600 mt-1 leading-relaxed">
        Este enlace sirve una sola vez.
      </p>

      <form onSubmit={enviar} noValidate className="mt-4 space-y-3">
        {restablecer.error && !errorClaveApi && (
          <Aviso tono="error">
            <span role="alert">{restablecer.error.message}</span>
            {/*
              Un token vencido o ya usado es el fallo más probable, y su salida
              es pedir otro: sin este enlace la pantalla no tiene continuación.
            */}
            {restablecer.error.codigo === 'validacion' && (
              <>
                {' '}
                <Link to="/recuperar" className="text-marca-600 underline hover:no-underline">
                  Pida un enlace nuevo
                </Link>
                .
              </>
            )}
          </Aviso>
        )}

        <label className="block">
          <span className="etiqueta">Contraseña nueva</span>
          <input
            className={`campo ${campos['clave'] || errorClaveApi ? 'campo-error' : ''}`}
            type="password"
            autoComplete="new-password"
            autoFocus
            value={clave}
            onChange={(e) => setClave(e.target.value)}
            disabled={restablecer.isPending}
          />
          {campos['clave'] || errorClaveApi ? (
            <p className="error-msg">{campos['clave'] ?? errorClaveApi}</p>
          ) : (
            <p className="ayuda">
              Mínimo {MINIMO_CLAVE} caracteres. No puede ser una contraseña conocida ni contener su
              correo o su nombre.
            </p>
          )}
        </label>

        <label className="block">
          <span className="etiqueta">Repita la contraseña</span>
          <input
            className={`campo ${campos['repetir'] ? 'campo-error' : ''}`}
            type="password"
            autoComplete="new-password"
            value={repetir}
            onChange={(e) => setRepetir(e.target.value)}
            disabled={restablecer.isPending}
          />
          {campos['repetir'] && <p className="error-msg">{campos['repetir']}</p>}
        </label>

        <button type="submit" className="btn btn-accion w-full" disabled={restablecer.isPending}>
          {restablecer.isPending ? 'Guardando…' : 'Guardar la contraseña'}
        </button>
      </form>
    </MarcoAcceso>
  )
}
